import ReactGA from "react-ga4";
import { getAppConfig } from "../../commons/util/appConfigHelper";
import { getLocalStorage } from "../storageUtil";

export const initGA4 = () => {
  if (window.__ga4Initialized) return;

  const GA4_ID = getAppConfig("GA4_ID");
  const GA4_ENABLED = getAppConfig("ENABLED_GA4");

  if (GA4_ID && GA4_ENABLED) {
    ReactGA.initialize(GA4_ID);
    window.__ga4Initialized = true;
  }
};

// Scope guard: only F&B / dine-in cart items (domain contains "food") should ever
// reach GA4 through this module. Every other vertical (cab, lounge, hotel, duty-free,
// parking, CBB) is intentionally left untouched.
const isFnbDomain = (domain) =>
  typeof domain === "string" && domain.toLowerCase().includes("food");

const hasValue = (value) => {
  if (value == null) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
};

const cleanValue = (value) => {
  if (!hasValue(value)) return undefined;
  return typeof value === "string" ? value.trim() : value;
};

const cleanString = (value) => {
  const cleaned = cleanValue(value);
  return cleaned == null ? undefined : String(cleaned).trim();
};

const round2 = (n) => {
  const value = Number(n);
  return Number.isFinite(value) ? Math.round(value * 100) / 100 : 0;
};

const normalizeQuantity = (quantity) => {
  const value = Number(quantity);
  return Number.isFinite(value) && value > 0 ? value : 1;
};

const CURRENCY_BY_COUNTRY = {
  US: "USD",
  DK: "DKK",
  GB: "GBP",
};

const resolveCurrency = (extra) => {
  if (extra?.currency) return extra.currency;
  const countryCode = getLocalStorage("config")?.countryCode || "US";
  return CURRENCY_BY_COUNTRY[countryCode] || "USD";
};

export const buildGa4Item = ({
  id,
  name,
  price,
  quantity = 1,
  brand,
  domain,
  fulfilmentType,
}) => {
  const itemId = cleanString(id);
  const itemName = cleanString(name) || itemId;

  return {
    ...(itemId && { item_id: itemId }),
    ...(itemName && { item_name: itemName }),
    price: round2(price),
    quantity: normalizeQuantity(quantity),
    ...(hasValue(brand) && { item_brand: cleanString(brand) }),
    ...(hasValue(domain) && { item_category: cleanString(domain) }),
    ...(hasValue(fulfilmentType) && {
      item_category2: cleanString(fulfilmentType),
    }),
  };
};

const fireEcommerceEvent = (eventName, items, extra = {}) => {
  const fnbItems = (items || []).filter((i) => i && isFnbDomain(i.item_category));
  if (!fnbItems.length) return;

  const value =
    extra.value != null
      ? round2(extra.value)
      : round2(fnbItems.reduce((sum, i) => sum + i.price * i.quantity, 0));

  const payload = {
    currency: resolveCurrency(extra),
    value,
    items: fnbItems,
    ...(extra.tax != null && { tax: round2(extra.tax) }),
    ...(extra.shipping != null && { shipping: round2(extra.shipping) }),
  };

  ReactGA.event(eventName, payload);
};

export const trackViewItem = (item, extra) => fireEcommerceEvent("view_item", [item], extra);

export const trackAddToCart = (item, extra) => fireEcommerceEvent("add_to_cart", [item], extra);

export const trackRemoveFromCart = (item, extra) => fireEcommerceEvent("remove_from_cart", [item], extra);

export const trackViewCart = (items, extra) => fireEcommerceEvent("view_cart", items, extra);

export const trackBeginCheckout = (items, extra) => fireEcommerceEvent("begin_checkout", items, extra);

export const trackPurchase = (transactionId, items, extra = {}) => {
  const fnbItems = (items || []).filter((i) => i && isFnbDomain(i.item_category));
  if (!fnbItems.length) return;

  const payload = {
    transaction_id: transactionId,
    currency: resolveCurrency(extra),
    value: round2(extra.value),
    items: fnbItems,
    ...(extra.tax != null && { tax: round2(extra.tax) }),
    ...(extra.shipping != null && { shipping: round2(extra.shipping) }),
  };

  ReactGA.event("purchase", payload);
};

export { isFnbDomain };
